import { ErrorView } from './ErrorView';
import { type ErrorConfigMap, ErrorType } from './types';

export const UNEXPECTED_ERROR_CODE = 'unexpected-error';
export enum GLOBAL_ERRORS {
  FATAL = 'fatal',
  NOT_FOUND = 'not-found',
  IMPLEMENTATION_DEFECT = 'implementation-defect',
  INTERNAL = 'internal',
  INTERNAL_1 = 'INTERNAL',
  INCOMPLETE_OPERATION = 'incomplete-operation',
  TENANT_NOT_FOUND = 'tenant-not-found',
  INVALID_CREDENTIALS = 'invalid-credentials',
  ACCOUNT_LOCKED = 'idp-locked',
  CAMERA_IN_USE = 'camera-in-use',
  CAMERA_ACCESS_BLOCKED = 'camera-access-blocked',
  CAMERA_UNKNOWN_ERROR = 'camera-unknown-error',
  NO_CAMERA_DEVICE = 'no-camera-device',
  DEVICE_DISCONNECTED = 'device-disconnected',
  IID_INVALID_CREDENTIALS = 'iid-invalid-credentials',
  FACTORS_INSUFFICIENT = 'factors-insufficient',
  USER_NOT_FOUND = 'user-not-found',
  OFFLINE = 'offline',
  CREDENTIAL_EXPIRED = 'credential-expired',
}

export enum FULL_PAGE_ERRORS {
  SYSTEM_ERROR = 'SYSTEM_ERROR',
  FATAL_ERROR = 'FATAL_ERROR',
}

export const errorConfiguration: ErrorConfigMap = {
  [GLOBAL_ERRORS.NOT_FOUND]: {
    errorCode: GLOBAL_ERRORS.NOT_FOUND,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.IMPLEMENTATION_DEFECT]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INTERNAL]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INTERNAL_1]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.INCOMPLETE_OPERATION]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.FATAL]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.TENANT_NOT_FOUND]: {
    errorCode: GLOBAL_ERRORS.TENANT_NOT_FOUND,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [FULL_PAGE_ERRORS.SYSTEM_ERROR]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.FULL_PAGE,
    errorView: <ErrorView />,
  },
  [FULL_PAGE_ERRORS.FATAL_ERROR]: {
    errorCode: UNEXPECTED_ERROR_CODE,
    type: 'warning',
    errorType: ErrorType.FULL_PAGE,
    errorView: <ErrorView />,
  },
  [GLOBAL_ERRORS.INVALID_CREDENTIALS]: {
    errorCode: GLOBAL_ERRORS.INVALID_CREDENTIALS,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.IID_INVALID_CREDENTIALS]: {
    errorCode: GLOBAL_ERRORS.IID_INVALID_CREDENTIALS,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.CAMERA_IN_USE]: {
    errorCode: GLOBAL_ERRORS.CAMERA_IN_USE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  // The camera conditions are registered here, but their banner copy is no longer
  // resolved from this map: it varies by which face ceremony was interrupted, so
  // both containers route camera codes through `resolveCameraNotification` (core's
  // `CAMERA_NOTIFICATIONS`) instead. These entries still carry the disposition —
  // recognized-as-a-warning rather than the generic `something-unexpected` — which
  // is what `tenant-lookup` tests with `errorCode in errorConfiguration`, and the
  // `description` keys keep that fallback path's sub-message correct.
  [GLOBAL_ERRORS.CAMERA_ACCESS_BLOCKED]: {
    errorCode: GLOBAL_ERRORS.CAMERA_ACCESS_BLOCKED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
    description: 'camera-access-blocked-sub-message',
  },
  [GLOBAL_ERRORS.CAMERA_UNKNOWN_ERROR]: {
    errorCode: GLOBAL_ERRORS.CAMERA_UNKNOWN_ERROR,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
    description: 'camera-unknown-error-sub-message',
  },
  [GLOBAL_ERRORS.NO_CAMERA_DEVICE]: {
    errorCode: GLOBAL_ERRORS.NO_CAMERA_DEVICE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
    description: 'no-camera-device-sub-message',
  },
  [GLOBAL_ERRORS.DEVICE_DISCONNECTED]: {
    errorCode: GLOBAL_ERRORS.DEVICE_DISCONNECTED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
    description: 'device-disconnected-sub-message',
  },
  [GLOBAL_ERRORS.ACCOUNT_LOCKED]: {
    errorCode: GLOBAL_ERRORS.ACCOUNT_LOCKED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.FACTORS_INSUFFICIENT]: {
    errorCode: GLOBAL_ERRORS.FACTORS_INSUFFICIENT,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.USER_NOT_FOUND]: {
    errorCode: GLOBAL_ERRORS.USER_NOT_FOUND,
    type: 'error',
    errorType: ErrorType.GLOBAL,
  },
  [GLOBAL_ERRORS.OFFLINE]: {
    errorCode: GLOBAL_ERRORS.OFFLINE,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
    description: 'offline-sub-message',
  },
  [GLOBAL_ERRORS.CREDENTIAL_EXPIRED]: {
    errorCode: GLOBAL_ERRORS.CREDENTIAL_EXPIRED,
    type: 'warning',
    errorType: ErrorType.GLOBAL,
    description: 'credential-expired-sub-message',
  },
};
